'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries, uploadFile } from '@eliadmin/core/src/api/general/general-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import type { InputProps } from '@eliadmin/core/src/components/ui/input';
import { Input } from '@eliadmin/core/src/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Control, FieldPath } from 'react-hook-form';
import { useFieldArray, useForm } from 'react-hook-form';
import { LuPlus } from 'react-icons/lu';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, enum as __eliadmin_zod_1, array as __eliadmin_zod_2, string as __eliadmin_zod_3, boolean as __eliadmin_zod_4 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { FiAlertCircle, FiCheckCircle, FiTrash2, FiUpload } from 'react-icons/fi';
import { PdfLogo } from '@eliadmin/core/src/components/product/detail/tabs/images/pdf-logo';
import { Cross2Icon } from '@radix-ui/react-icons';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { createWholesaleRegistration } from '@eliadmin/core/src/api/wholesale/wholesale-api';
import { Container } from '@eliadmin/core/src/components/ui/container';
import Link from '@eliadmin/core/src/components/link';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { SignInDialog } from "@eliadmin/core/src/components/dialog/lazy-dialogs";
export type WholesaleFormValues = z.infer<ReturnType<typeof wholesalerSchema>>;

const eshopOptions = [
  { value: 'eshop', label: 'text298' },
  { value: 'bms', label: 'text299' },
  { value: 'eshop_bms', label: 'text300' },
];

export function WholesaleForm() {
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();

  const me = useCustomerStore(state => state.me);
  const isInitialized = useCustomerStore(state => state.isInitialized);

  const { formValidationMessages } = useFormValidationMessages(t);
  const openDialog = useDialogStore(s => s.open);

  const form = useForm<WholesaleFormValues>({
    resolver: zodResolver(wholesalerSchema(formValidationMessages)),
    defaultValues: {
      selectType: 'eshop',
      company: '',
      name: '',
      surname: '',
      ico: '',
      ic_dph: '',
      dic: '',
      address: '',
      street_number: '',
      city: '',
      postcode: '',
      country: {
        id: '',
      },
      phone: '',
      email: '',
      files: [],
      domains: [],
      addresses: [],
      tos: false,
    },
  });

  const editedFields = useRef(new Set<string>());
  // Subscribe to RHF dirtiness even when no visible control consumes it.
  void form.formState.dirtyFields;

  useEffect(() => {
    const subscription = form.watch((_, { name, type }) => {
      // RHF emits "change" for control events, but not for setValue. Remember
      // edit intent even after a user clears a field back to its empty default.
      if (name && type === 'change') editedFields.current.add(name);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const customer = me?.customer;
  useEffect(() => {
    if (!isInitialized) return;
    const values = {
      name: customer?.name ?? '',
      surname: customer?.surname ?? '',
      phone: customer?.phone ?? '',
      email: customer?.email ?? '',
      company: customer?.company ?? '',
      ico: customer?.ico ?? '',
      dic: customer?.dic ?? '',
      ic_dph: customer?.ic_dph ?? '',
      address: customer?.address ?? '',
      street_number: customer?.street_number ?? '',
      city: customer?.city ?? '',
      postcode: customer?.postcode ?? '',
      'country.id': customer?.country?.id != null ? String(customer.country.id) : '',
    };

    for (const name of Object.keys(values) as (keyof typeof values)[]) {
      if (editedFields.current.has(name) || form.getFieldState(name).isDirty) continue;
      const value = values[name];
      if (form.getValues(name) === value) continue;
      // Also replace/clear untouched automatic values on account switch/logout.
      // Establish each field's baseline so later field-array operations cannot
      // reclassify automatic profile values as manual edits. Keep its errors.
      // Never reset the whole form: arrays, uploads and consent belong to it.
      form.resetField(name, {
        defaultValue: value,
        keepError: true,
        keepTouched: true,
      });
    }
  }, [customer, form, isInitialized]);

  const domainsArray = useFieldArray({
    control: form.control,
    name: 'domains',
  });

  const addressesArray = useFieldArray({
    control: form.control,
    name: 'addresses',
  });

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleFileChange = (file: File | undefined) => {
    if (!file) return;
    setSelectedFiles(prevSelectedFiles => {
      const fileIndex = prevSelectedFiles.findIndex(f => f.name === file.name);

      if (fileIndex >= 0) {
        return prevSelectedFiles.filter((_, index) => index !== fileIndex);
      } else {
        return [...prevSelectedFiles, file];
      }
    });
  };

  const { data: countries = { data: [] }, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const handleSubmit = form.handleSubmit(async data => {
    form.setValue('files', []);
    const uploadPromises = selectedFiles.map(async file => {
      const formData = new FormData();
      formData.append('file', file);
      return uploadFile({
        authToken: me?.authToken || '',
        formData,
        locale,
      })
        .then(res => {
          return { id: res.data.id };
        })
        .catch(() => {
          return null;
        });
    });

    try {
      const uploadedFiles = await Promise.all(uploadPromises);
      const nonNullUploadedFiles = uploadedFiles.filter(file => file !== null) as { id: number }[];

      const typeId = data.selectType === 'eshop_bms' ? 3 : data.selectType === 'eshop' ? 1 : 2;
      const transformedRequestData = {
        ...data,
        type: {
          id: typeId,
        },
        files: nonNullUploadedFiles,
      };

      await createWholesaleRegistration(transformedRequestData, me?.authToken || '');
      setIsFormSubmitted(true);
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
      } else {
        // eslint-disable-next-line no-console
        process.env.NODE_ENV !== 'production' && console.log('Error during wholesale registration');
      }
    }
  });

  if (isFormSubmitted) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-3 sm:gap-6">
        <div className="flex h-32 w-32 items-center justify-center rounded-full bg-success text-success-foreground">
          <FiCheckCircle className="h-[56px] w-[56px]" />
        </div>
        <h3 className="text-3xl font-bold">{t('text643')}</h3>
        <p>{t('text774')}</p>
        <Button asChild variant={'outline'}>
          <Link href="/">{t('text725')}</Link>
        </Button>
      </div>
    );
  }

  if (!isInitialized) {
    return (
      <div className="flex w-full items-center justify-center">
        <Spinner className="h-14 w-14" />
      </div>
    );
  }

  if (!me) {
    return (
      <Container className="flex flex-col space-y-8 p-0">
        <Card className="maw-w-[850px] h-[132px] rounded-lg bg-destructive p-0 text-destructive-foreground">
          <CardContent className="flex h-full w-full items-center justify-between gap-8 p-8">
            <FiAlertCircle className="h-11 w-11" />
            <p className="flex-1 text-xl font-bold">{t('text763')}</p>
            <Button
              className="bg-primary-foreground text-destructive-foreground hover:bg-primary-foreground"
              onClick={() => {
                openDialog({
                  id: SignInDialog.id,
                  content: <SignInDialog inCart={false} {...{ stayOnCurrentPage: true }} />,
                });
              }}
            >
              {t('text189')}
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (me?.customer.is_wholesaler) {
    return (
      <Container className="flex flex-col space-y-8 p-0">
        <Card className="maw-w-[850px] h-[132px] rounded-lg bg-success p-0 text-success-foreground">
          <CardContent className="flex h-full w-full items-center justify-between gap-8 p-8">
            <FiCheckCircle className="h-11 w-11" />
            <p className="flex-1 text-xl font-bold">{t('text765')}</p>
          </CardContent>
        </Card>
      </Container>
    );
  }
  const selectTypeValue = form.watch('selectType');

  return (
    <Form {...form}>
      <form className="grid grid-cols-12 gap-4" onSubmit={handleSubmit}>
        <SectionHeader title={t('text766')} />
        <Field
          control={form.control}
          name="selectType"
          type="select"
          options={eshopOptions}
          required
        />

        {selectTypeValue != 'bms' &&
          domainsArray.fields.map((field, i) => {
            return (
              <div className="col-span-12 grid grid-cols-12" key={field.id}>
                <Field
                  control={form.control}
                  name={`domains.${i}.url`}
                  label={t('text325') + ` (${i + 1})`}
                  placeholder={t('text325')}
                  after={
                    <Button
                      size="icon-sm"
                      onClick={() => domainsArray.remove(i)}
                      className="h-12 w-12 rounded-lg p-2"
                      type="button"
                      variant="outline"
                    >
                      <FiTrash2 className="h-5 w-5 text-destructive-foreground" />
                    </Button>
                  }
                />
              </div>
            );
          })}

        {selectTypeValue != 'bms' && (
          <div className="col-span-12">
            <Button
              type="button"
              onClick={() => {
                domainsArray.append({ url: '' });
              }}
              variant="dark"
            >
              <LuPlus className="mr-2 h-4 w-4" />
              {t('text326')}
            </Button>
          </div>
        )}

        <SectionHeader title={t('text324')} />
        {/* add button */}
        <Field
          control={form.control}
          name="company"
          label={t('text328')}
          placeholder={t('text328')}
          required
          isFullWidth
        />

        <Field
          control={form.control}
          name="name"
          label={t('text33')}
          placeholder={t('text33')}
          required
        />
        <Field
          control={form.control}
          name="surname"
          label={t('text34')}
          placeholder={t('text34')}
          required
        />
        <Field
          control={form.control}
          name="ico"
          label={t('text42')}
          placeholder={t('text42')}
          required
        />
        <Field
          control={form.control}
          name="ic_dph"
          label={t('text44')}
          placeholder={t('text44')}
          required
        />
        <Field
          control={form.control}
          name="dic"
          label={t('text43')}
          placeholder={t('text43')}
          required
        />

        {/* address */}
        <SectionHeader title={t('text157')} />
        <Field
          control={form.control}
          name="address"
          label={t('text38')}
          placeholder={t('text38')}
          required
        />
        <Field
          control={form.control}
          name="street_number"
          label={t('text438')}
          placeholder={t('text438')}
          required
        />
        <Field
          control={form.control}
          name="city"
          label={t('text39')}
          placeholder={t('text39')}
          required
        />
        <Field
          control={form.control}
          name="postcode"
          label={t('text40')}
          placeholder={t('text40')}
          required
        />

        <Field
          control={form.control}
          name="country.id"
          type="select"
          label={t('text41')}
          placeholder={t('text41')}
          isDisabled={isCountriesLoading}
          options={countries.data.map(c => ({ value: c.id, label: t(c.name) }))}
          required
        />

        {/* store addresses */}
        {selectTypeValue != 'eshop' &&
          addressesArray.fields.map((field, i) => {
            return (
              <Card key={field.id} className="col-span-12 rounded-none">
                <CardContent className="grid grid-cols-12 gap-2 p-6 pt-8">
                  <div className="col-span-12 flex items-center gap-4">
                    <SectionHeader title={t('text330')} />
                    <Button
                      size="icon-sm"
                      onClick={() => addressesArray.remove(i)}
                      className="h-12 w-12 rounded-lg p-2"
                      type="button"
                      variant="outline"
                    >
                      <FiTrash2 className="h-5 w-5 text-destructive-foreground" />
                    </Button>
                  </div>
                  <Field
                    name={`addresses.${i}.name`}
                    control={form.control}
                    label={t('text331')}
                    placeholder={t('text331')}
                    required
                  />

                  <Field
                    name={`addresses.${i}.address`}
                    control={form.control}
                    label={t('text38')}
                    placeholder={t('text38')}
                    required
                  />

                  <Field
                    name={`addresses.${i}.city`}
                    control={form.control}
                    label={t('text39')}
                    placeholder={t('text39')}
                    required
                  />

                  <Field
                    name={`addresses.${i}.postcode`}
                    control={form.control}
                    label={t('text40')}
                    placeholder={t('text40')}
                    required
                  />

                  <Field
                    name={`addresses.${i}.country.id`}
                    control={form.control}
                    type="select"
                    label={t('text41')}
                    placeholder={t('text41')}
                    options={countries.data.map(c => ({ value: c.id, label: t(c.name) }))}
                    required
                  />
                </CardContent>
              </Card>
            );
          })}

        {selectTypeValue != 'eshop' && (
          <div className="col-span-12">
            <Button
              variant="dark"
              type="button"
              onClick={() => {
                addressesArray.append({
                  name: '',
                  address: '',
                  city: '',
                  street_number: '',
                  postcode: '',
                  country: {
                    id: '',
                  },
                });
              }}
            >
              <LuPlus className="mr-2 h-4 w-4" />
              {t('text329')}
            </Button>
          </div>
        )}

        {/* additional */}
        <SectionHeader title={t('text767')} />
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem required className={cn('col-span-12 md:col-span-6')}>
              <FormLabel>{t('text36')}</FormLabel>
              <FormControl>
                <PhoneInputField
                  {...field}
                  disableDialCodePrefill
                  onChange={value => {
                    // The phone control also reports synchronization on mount.
                    // An unchanged programmatic value is not a customer edit.
                    if (value !== field.value) field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Field
          control={form.control}
          name="email"
          label={t('text35')}
          placeholder={t('text35')}
          isDisabled={Boolean(me?.customer.email)}
          required
        />
        {/* <Field
          control={form.control}
          name="password"
          label={t('text191')}
          placeholder={t('text191')}
          type="password"
          required
        />
        <Field
          control={form.control}
          name="password_confirmation"
          label={t('text263')}
          placeholder={t('text263')}
          type="password"
          required
        /> */}

        <SectionHeader title={t('text333')} subtitle={t('text334')} />
        <div className="col-span-12">
          {selectedFiles.map((file: any) => (
            <div key={file.name} className="flex flex-row items-center gap-2 py-2">
              <Cross2Icon onClick={() => handleFileChange(file)} />
              <PdfLogo />
              <span className="text-sm font-normal text-muted-foreground">{file.name}</span>
            </div>
          ))}
          <Input
            type="file"
            name="files"
            className={{
              wrapper: 'flex h-40 border-dotted border-border bg-transparent',
              main: 'hidden',
              before: 'w-full flex-1',
            }}
            id="file-upload"
            before={
              <label htmlFor="file-upload" className="flex h-full w-full">
                <div className="flex h-full w-full flex-col items-center justify-around rounded-[16px] border border-dashed border-border py-4">
                  <div className="rounded-lg bg-input p-5">
                    <FiUpload className="h-5 w-5 text-foreground" />
                  </div>
                  <span className="text-sm font-normal text-muted-foreground">{t('text335')}</span>
                  <p className="text-sm font-normal text-muted-foreground">{t('text768')}</p>
                </div>
              </label>
            }
            onChange={e => handleFileChange(e.target.files?.[0])}
          />
        </div>

        <div className="col-span-12 flex flex-row items-center gap-2 py-4">
          <FormField
            control={form.control}
            name="tos"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-start space-x-3 space-y-0',
                  'ea-register-form__tos-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>{t('text336')}</FormLabel>
              </FormItem>
            )}
          />
        </div>
        <div className="col-span-12 flex justify-end">
          <Button variant="accent" type="submit">
            {t('text192')}
          </Button>
        </div>
      </form>
    </Form>
  );
}

type FieldProps = {
  control: Control<WholesaleFormValues>;
  name: FieldPath<WholesaleFormValues>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  type?: InputProps['type'];
  options?: { value: string | number; label: string }[];
  isFullWidth?: boolean;
  isDisabled?: boolean;
  after?: React.ReactNode;
};

function Field(props: FieldProps) {
  const { t } = useTranslations();
  const content = useMemo(() => {
    if (props.type === 'select') {
      if (!props.options) {
        throw new Error('Select type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              {props.label && <FormLabel>{props.label}</FormLabel>}
              <FormControl>
                <Select
                  value={String(field.value)}
                  onValueChange={value => {
                    // No option has an empty value. Radix's hidden select can
                    // report one while country options are still loading.
                    if (value !== '' && value !== field.value) field.onChange(value);
                  }}
                  disabled={props.isDisabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={props.placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {props.options?.map(o => (
                      <SelectItem key={o.value} value={String(o.value)}>
                        {t(o.label as any)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={props.control}
        name={props.name}
        render={({ field }) => (
          <FormItem required={props.required}>
            {props.label && <FormLabel>{props.label}</FormLabel>}
            <FormControl>
              <div className="flex w-full flex-row items-center space-x-2">
                <Input
                  placeholder={props.placeholder}
                  type={props.type}
                  {...(field as any)}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                  afterOuter={props.after}
                  className={{ wrapper: 'w-auto flex-1' }}
                />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }, [
    props.after,
    props.control,
    props.isDisabled,
    props.label,
    props.name,
    props.options,
    props.placeholder,
    props.required,
    props.type,
    t,
  ]);

  return (
    <div
      className={cn('col-span-12 md:col-span-6', {
        ['md:col-span-12']: props.isFullWidth,
      })}
    >
      {content}
    </div>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="col-span-12 flex flex-col justify-start gap-2 md:items-start">
      <h4 className="text-xl font-semibold text-foreground">{title}</h4>
      {subtitle && <span className="text-sm font-normal text-muted-foreground">{subtitle}</span>}
    </div>
  );
}

export const wholesalerSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
    selectType: __eliadmin_zod_1(['eshop_bms', 'eshop', 'bms']).default('eshop'),
    domains: __eliadmin_zod_2(__eliadmin_zod_0({ url: __eliadmin_zod_3() })).default([]),

    company: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    name: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    ico: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    ic_dph: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    dic: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),

    address: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    city: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    street_number: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    postcode: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    country: __eliadmin_zod_0({
      id: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    }),

    addresses: __eliadmin_zod_2(
        __eliadmin_zod_0({
          name: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
          address: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
          city: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
          street_number: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
          postcode: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
          country: __eliadmin_zod_0({
            id: __eliadmin_zod_3().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
          }),
        }),
      )
      .default([]),

    phone: __eliadmin_zod_3().min(12, VM.CANT_BE_EMPTY_PHONE_VALIDATION_MESSAGE()),
    email: __eliadmin_zod_3().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),

    files: __eliadmin_zod_2(__eliadmin_zod_0({ id: __eliadmin_zod_3() })).default([]),
    tos: __eliadmin_zod_4().refine(v => v, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    // password: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    // password_confirmation: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
  });
